import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Star, X } from 'lucide-react';
import { Button } from './Button';
import { clsx } from 'clsx';
import { api } from '../lib/axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface DaycareReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    dayCareBookingId: string;
    daycareName?: string;
    onSuccess: () => void;
}

export const DaycareReviewModal: React.FC<DaycareReviewModalProps> = ({ isOpen, onClose, dayCareBookingId, daycareName, onSuccess }) => {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t } = useTranslation();

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (rating === 0) {
            toast.error(t('review.selectRating', 'Please select a rating'));
            return;
        }

        setIsSubmitting(true);
        try {
            await api.post('/daycare/reviews', {
                dayCareBookingId,
                rating,
                comment
            });
            toast.success(t('review.thankYou', 'Thank you for your feedback!'));
            onSuccess();
            onClose();
        } catch (error: any) {
            toast.error(error.response?.data?.error || t('review.failedToSubmit', 'Failed to submit review'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-xl z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="font-bold text-slate-800">{t('review.rateBoardingAndService', 'Rate Boarding & Service')}</h2>
                    <button onClick={onClose} className="p-1 hover:bg-slate-200 rounded-full transition-colors">
                        <X size={18} className="text-slate-500" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    <div className="text-center">
                        <p className="text-sm text-slate-500 mb-4 uppercase tracking-widest font-black text-[10px]">{t('review.daycareStayQuestion', "How was your pet's stay with {{name}}?", { name: daycareName || t('common.theDaycare', 'the Daycare') })}</p>
                        <div className="flex justify-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <div
                                    key={star}
                                    className="relative cursor-pointer transition-transform hover:scale-110 active:scale-95"
                                    onMouseMove={(e) => {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const x = e.clientX - rect.left;
                                        const val = x < rect.width / 2 ? star - 0.5 : star;
                                        setHover(val);
                                    }}
                                    onMouseLeave={() => setHover(0)}
                                    onClick={() => setRating(hover)}
                                >
                                    <Star
                                        className={clsx(
                                            "w-10 h-10",
                                            (hover || rating) >= star
                                                ? "text-yellow-400 fill-current"
                                                : (hover || rating) >= star - 0.5
                                                    ? "text-yellow-400"
                                                    : "text-gray-200"
                                        )}
                                    />
                                    {((hover || rating) >= star - 0.5 && (hover || rating) < star) && (
                                        <div className="absolute inset-0 overflow-hidden w-[50%] pointer-events-none">
                                            <Star className="w-10 h-10 text-yellow-400 fill-current" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="mt-2 h-4">
                            {rating > 0 && (
                                <span className="text-xs font-bold text-yellow-600 uppercase tracking-widest">
                                    {t('review.starRating', '{{rating}} Star Rating', { rating })}
                                </span>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-[10px] font-black text-slate-400 mb-2 uppercase tracking-widest">{t('review.feedbackCommentOptional', 'Feedback Comment (Optional)')}</label>
                        <textarea
                            className="w-full rounded-xl border-slate-200 bg-slate-50 focus:border-brand-500 focus:ring-brand-500 text-sm p-3 min-h-[100px] outline-none transition-all"
                            placeholder={t('review.shareExperience', 'Share your experience...')}
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    <Button
                        fullWidth
                        type="submit"
                        disabled={rating === 0 || isSubmitting}
                        className="bg-brand-600 text-white font-bold rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] hover:bg-brand-700 hover:shadow-[0_8px_30px_rgb(0,0,0,0.16)] transition-all hover:-translate-y-0.5 border-transparent h-12"
                    >
                        {isSubmitting ? t('review.submitting', 'Submitting Review...') : t('review.submitReview', 'Submit {{rating}} Star Review', { rating })}
                    </Button>
                </form>
            </div>
        </div>,
        document.body
    );
};



